import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { InputAreaFileComponent } from 'app/shared/components/input-area-file/input-area-file.component';
import { FileSrcPipe } from 'app/shared/pipes/file-src.pipe';

@Component({
  selector: 'casa-doc-upload-preview-step',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputAreaFileComponent,
    FileSrcPipe,
  ],
  templateUrl: './upload-preview-step.component.html',
  styleUrl: './upload-preview-step.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadPreviewStepComponent {
  #destroyRef = inject(DestroyRef);

  files = input<File[]>([]);
  filesChange = output<File[]>();
  proceed = output<void>();

  readonly filesControl = new FormControl<File[]>([], { nonNullable: true });

  constructor() {
    effect(
      () => {
        const files = this.files();
        if (!this.#sameFiles(files, this.filesControl.value)) {
          this.filesControl.setValue(files, { emitEvent: false });
        }
      },
      {
        allowSignalWrites: true,
      }
    );

    this.filesControl.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((files) => this.filesChange.emit(files ?? []));
  }

  onClear() {
    this.filesControl.setValue([]);
  }

  onReplaceSelection(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    this.filesControl.setValue(files);
    input.value = '';
  }

  onProceed() {
    if (!this.files().length) {
      return;
    }

    this.proceed.emit();
  }

  #sameFiles(left: File[], right: File[]) {
    if (left.length !== right.length) {
      return false;
    }

    return left.every((file, index) => file === right[index]);
  }
}
